import { Box, TextField, alpha } from "@mui/material";
import { KeyboardEvent, useRef } from "react";

interface Props {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  /** "lg" replica `.ld-verify-otp-lg` (pantallas de verificación con OTP como protagonista). */
  size?: "md" | "lg";
  disabled?: boolean;
}

const DIMENSIONS = {
  md: { width: 48, height: 56, fontSize: 20, gap: 1 },
  lg: { width: 64, height: 72, fontSize: 28, gap: 1.5 },
} as const;

export const OtpInput = ({ length = 4, value, onChange, size = "md", disabled = false }: Props) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = value.padEnd(length, " ").slice(0, length).split("");
  const dim = DIMENSIONS[size];

  const handleChange = (i: number, ch: string) => {
    const digit = ch.replace(/[^\d]/g, "").slice(-1);
    const next = chars.slice();
    next[i] = digit || " ";
    onChange(next.join("").replace(/\s+$/g, ""));
    if (digit && refs.current[i + 1]) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Backspace" && !chars[i].trim() && refs.current[i - 1]) refs.current[i - 1]?.focus();
  };

  return (
    <Box sx={{ display: "flex", gap: dim.gap }}>
      {chars.map((c, i) => (
        <TextField
          autoComplete="off"
          key={i}
          inputRef={(el: HTMLInputElement | null) => (refs.current[i] = el)}
          value={c.trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
            style: { textAlign: "center", fontSize: dim.fontSize, fontWeight: 600, padding: 0 },
          }}
          sx={{
            width: dim.width,
            // Base `.otp input` del diseño: 1.5px de borde; al enfocar, borde de marca con halo; ya capturada,
            // fondo en el tono claro de la paleta. El gris del spec (#D6DBE3) casi no se distinguía sobre la
            // tarjeta blanca, así que el borde vacío se deriva de `primary.main` al 35% y la celda lleva un tinte.
            "& .MuiOutlinedInput-root": {
              height: dim.height,
              borderRadius: "10px",
              fontFamily: "Outfit",
              color: "primary.dark",
              bgcolor: (theme) => (c.trim() ? theme.palette.primary.base : alpha(theme.palette.primary.base, 0.45)),
              transition: "box-shadow .15s ease, background-color .15s ease",
              "& fieldset": {
                borderWidth: "1.5px",
                borderColor: (theme) => alpha(theme.palette.primary.main, c.trim() ? 0.3 : 0.35),
              },
              "&:hover fieldset": { borderColor: (theme) => alpha(theme.palette.primary.main, 0.6) },
              "&.Mui-focused fieldset": { borderWidth: "1.5px", borderColor: "primary.main" },
              "&.Mui-focused": { boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}` },
            },
          }}
        />
      ))}
    </Box>
  );
};
